/**
 * Impersonation — staff "log in as user" support.
 *
 * Flow (audit P0-7 — handoff-code exchange):
 *   1. Admin (shaka-admin) opens shaka-deck with `?impersonationCode=<code>`
 *      pointing at the active deck deploy. The code is a 32-byte
 *      base64url-safe one-shot credential, valid for 60 seconds.
 *   2. Bootstrap (`adoptImpersonationFromUrl`) reads the code, immediately
 *      strips it from the URL, POSTs to
 *      `/api/admin/impersonation/handoff/<code>/exchange/` to receive the
 *      actual JWT, then decodes the claims (impersonating_by, scope,
 *      expires_at) and stashes the token in sessionStorage (NOT
 *      localStorage — bound to the tab).
 *   3. The apiClient injects `Authorization: Bearer <token>` on every
 *      request when `getImpersonationToken()` returns a value, and refuses
 *      unsafe HTTP verbs when scope === 'read_only'.
 *   4. `<ImpersonationBanner />` shows a persistent red ribbon at the top
 *      of every page until `endImpersonation()` runs (manual button or
 *      token expiration).
 *
 * Why a handoff code instead of putting the JWT in the URL:
 *   - The JWT carries minutes-to-hours of write access; a leaked URL
 *     keeps that credential alive in browser history, Referer headers,
 *     and CDN logs for as long as the JWT's exp claim.
 *   - The handoff code is single-use and expires in 60 s server-side,
 *     so the URL leak window shrinks from hours to roughly one round
 *     trip.
 *
 * Why sessionStorage instead of memory-only:
 *   - Survives page refresh inside the same tab (UX expectation).
 *   - Doesn't bleed across tabs — opening a normal deck tab while
 *     impersonating in another stays clean.
 *   - Cleared automatically when the tab closes.
 *
 * Why NOT a custom cookie:
 *   - The host-level access_token cookie is httpOnly and we don't want to
 *     overwrite it. The Bearer-header approach co-exists with the cookie
 *     auth path: backend's JWT middleware accepts EITHER the cookie OR
 *     the Authorization header.
 */

import { deriveApiBaseUrl } from '@/shared/services/apiBase'
import { decodeJwt } from '@/shared/utils/jwt'

const STORAGE_KEY = 'shaka_impersonation_token'

export interface ImpersonationContext {
  /** The raw JWT we forward as Bearer on every request. */
  token: string
  /** Staff member who initiated the session (email + uuid). */
  impersonatingBy: string
  impersonatingByEmail: string
  /** 'read_only' blocks unsafe HTTP verbs. 'full' allows all. */
  scope: 'read_only' | 'full'
  /** ISO timestamp when this session expires; client refuses to send the
   *  token past this and runs `endImpersonation()`. */
  expiresAt: string
}

interface ImpersonationJwtClaims {
  impersonating_by?: string
  impersonating_by_email?: string
  impersonation_scope?: 'read_only' | 'full'
  impersonation_expires_at?: string
  exp?: number
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Capture an impersonation handoff code from the URL on app startup,
 * exchange it server-side for the actual JWT, store the JWT in
 * sessionStorage, and return the parsed context.
 *
 * Idempotent. Returns the stored context if no handoff code is on the
 * URL but a JWT is already in sessionStorage. Returns null when there's
 * nothing to adopt.
 *
 * Sync wrapper that fires the async exchange and resolves the promise
 * via a callback so consumers (root bootstrap) can `await` properly.
 */
export async function adoptImpersonationFromUrl(): Promise<ImpersonationContext | null> {
  const url = new URL(window.location.href)
  const code = url.searchParams.get('impersonationCode')

  // Strip the handoff code unconditionally — even on failure we don't
  // want it sitting in history. Do this before any await so a slow
  // exchange doesn't leak through the address bar.
  if (code) {
    url.searchParams.delete('impersonationCode')
    const newQuery = url.searchParams.toString()
    const newUrl = url.pathname + (newQuery ? `?${newQuery}` : '') + url.hash
    window.history.replaceState({}, '', newUrl)

    try {
      const token = await exchangeHandoffCode(code)
      const ctx = parseImpersonationToken(token)
      if (!ctx) {
        console.warn('[impersonation] exchanged code but failed to parse claims; ignoring.')
        return getStoredImpersonation()
      }
      sessionStorage.setItem(STORAGE_KEY, token)
      return ctx
    } catch (err) {
      console.warn('[impersonation] handoff exchange failed:', err)
      return getStoredImpersonation()
    }
  }

  return getStoredImpersonation()
}

async function exchangeHandoffCode(code: string): Promise<string> {
  const apiBase = deriveApiBaseUrl()
  const res = await fetch(
    `${apiBase}/api/admin/impersonation/handoff/${encodeURIComponent(code)}/exchange/`,
    {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    },
  )
  if (!res.ok) {
    throw new Error(`exchange_failed_status_${res.status}`)
  }
  const body = await res.json() as { token?: string }
  if (!body.token) {
    throw new Error('exchange_missing_token')
  }
  return body.token
}

/** Returns the stored impersonation context, or null. Cheap — runs on every request. */
export function getStoredImpersonation(): ImpersonationContext | null {
  const token = sessionStorage.getItem(STORAGE_KEY)
  if (!token) return null
  const ctx = parseImpersonationToken(token)
  if (!ctx) {
    sessionStorage.removeItem(STORAGE_KEY)
    return null
  }
  // Self-expire if past expiration
  if (Date.now() > new Date(ctx.expiresAt).getTime()) {
    sessionStorage.removeItem(STORAGE_KEY)
    return null
  }
  return ctx
}

/** Convenience for the apiClient: returns just the bearer token, or null. */
export function getImpersonationToken(): string | null {
  return getStoredImpersonation()?.token ?? null
}

/** Convenience for the apiClient: returns the scope, or null. */
export function getImpersonationScope(): 'read_only' | 'full' | null {
  return getStoredImpersonation()?.scope ?? null
}

export function isImpersonating(): boolean {
  return getStoredImpersonation() !== null
}

/**
 * Tear down the impersonation session. Triggers a hard reload so:
 *   - the apiClient drops the Bearer header,
 *   - any cached React Query data scoped to the impersonated user is gone,
 *   - the user lands on whatever route they were on, fresh.
 *
 * Best-effort: pings `/api/admin/impersonation/end/` first so the audit
 * trail closes (matches the `support.impersonation_started` event). If
 * the call fails (network blip, expired token), we proceed anyway —
 * UX should never block on audit logging.
 */
export function endImpersonation(): void {
  const ctx = getStoredImpersonation()
  if (ctx) {
    // Fire-and-forget. We don't await — the reload below will tear
    // down the page anyway, and `keepalive` lets the request survive
    // navigation up to ~64KB body, more than enough here.
    try {
      const apiBase = deriveApiBaseUrl()
      void fetch(`${apiBase}/api/admin/impersonation/end/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${ctx.token}`,
        },
        body: JSON.stringify({ reason: 'user_clicked_end' }),
        keepalive: true,
      }).catch(() => undefined)
    } catch {
      // Browsers without keepalive support (none of our targets) will throw — ignore.
    }
  }

  sessionStorage.removeItem(STORAGE_KEY)
  // Hard reload — softer alternatives (state reset) are tricky because
  // dozens of components hold derived data. A reload is correct and safe.
  window.location.reload()
}

// ── Internals ─────────────────────────────────────────────────────────────────

function parseImpersonationToken(token: string): ImpersonationContext | null {
  let claims: ImpersonationJwtClaims | null
  try {
    claims = decodeJwt<ImpersonationJwtClaims>(token)
  } catch {
    return null
  }
  if (!claims || !claims.impersonating_by) return null

  // expires_at — prefer the explicit ISO claim; fall back to JWT exp (seconds since epoch).
  const expiresAt =
    claims.impersonation_expires_at
    || (claims.exp ? new Date(claims.exp * 1000).toISOString() : null)
  if (!expiresAt) return null

  return {
    token,
    impersonatingBy: claims.impersonating_by,
    impersonatingByEmail: claims.impersonating_by_email || '',
    scope: claims.impersonation_scope === 'full' ? 'full' : 'read_only',
    expiresAt,
  }
}
